/**
 * Forward geocode via OpenStreetMap Nominatim (free; use sparingly — cache results in Firestore).
 * https://operations.osmfoundation.org/policies/nominatim/
 *
 * Best practice: separate sub-unit (suite/apt) from the street line and use structured search
 * (`street` + `city` + `postalcode`) so the civic number is not “lost” vs. a single free-text query.
 */

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Strip unit / suite / apartment tokens from a single-line civic string (legacy / paste).
 * Assessment parcels are lot polygons; unit numbers are not geocoded as separate points.
 */
export function addressLineForCalgaryGeocode(streetAddress) {
  let s = String(streetAddress ?? "")
    .trim()
    .replace(/\s+/g, " ");
  if (!s) return "";

  s = s.replace(/\s*,\s*(?:unit|suite|ste|apt)\s*[#]?\s*[\w/-]+\s*$/i, "");
  s = s.replace(/\s+(?:unit|suite|ste|apt)\s+[#]?\s*[\w/-]+\s*$/i, "");
  s = s.replace(/\s+#\s*[\w/-]+\s*$/i, "");

  s = s.trim();

  s = s
    .replace(/^\s*(?:unit|suite|ste|apt)\s+[#]?\s*[\w/-]+\s*[-–—,;]\s*/i, "")
    .trim();
  s = s.replace(/^\s*(?:unit|suite|ste|apt)\s+[#]?\s*[\w/-]+\s*,\s*/i, "").trim();
  s = s.replace(/^\s*#\s*[\w/-]+\s*[,;-]\s*/i, "").trim();

  const commaUnit = s.match(/^(\d{1,4})\s*,\s*(\d{3,5})\s+(.+)$/);
  if (commaUnit?.[3]) {
    s = `${commaUnit[2]} ${commaUnit[3]}`.trim();
  }

  return s.replace(/\s+/g, " ").trim();
}

function extractLeadingHouseNumber(street) {
  const m = String(street || "")
    .trim()
    .match(/^(\d+[a-zA-Z]?)\b/);
  return m ? m[1] : null;
}

/**
 * Prefer Nominatim hits whose label still contains the civic number (avoids “66 Ave SW” without 3130).
 */
function pickBestNominatimHit(streetLine, hits) {
  if (!Array.isArray(hits) || hits.length === 0) return null;
  const house = extractLeadingHouseNumber(streetLine);
  if (!house) return hits[0];

  const re = new RegExp(`\\b${house.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
  const withNum = hits.find((h) => h && re.test(String(h.display_name || "")));
  return withNum || hits[0];
}

async function nominatimSearchFreeform(q, { limit = 8 } = {}) {
  const url =
    "https://nominatim.openstreetmap.org/search?" +
    new URLSearchParams({
      format: "json",
      limit: String(limit),
      addressdetails: "1",
      q,
    }).toString();

  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
    },
  });

  if (!res.ok) {
    return { ok: false, error: `HTTP ${res.status}`, hits: [] };
  }

  const data = await res.json();
  const hits = Array.isArray(data) ? data : [];
  const hit = pickBestNominatimHit(q.split(",")[0] || q, hits);
  if (!hit || hit.lat == null || hit.lon == null) {
    return { ok: false, error: "No results", hits: [] };
  }

  const lat = Number(hit.lat);
  const lng = Number(hit.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { ok: false, error: "Invalid coordinates", hits: [] };
  }

  return {
    ok: true,
    lat,
    lng,
    displayName: hit.display_name || null,
    hit,
    hits,
  };
}

async function nominatimSearchStructured(
  { street, city, state, country, postalcode },
  { limit = 8 } = {}
) {
  const st = String(street || "").trim();
  if (!st) {
    return { ok: false, error: "Address too short", hits: [] };
  }

  const params = new URLSearchParams({
    format: "json",
    limit: String(limit),
    addressdetails: "1",
    street: st,
    city: String(city || "Calgary").trim() || "Calgary",
    state: String(state || "Alberta").trim() || "Alberta",
    country: String(country || "Canada").trim() || "Canada",
  });
  const pc = String(postalcode || "").trim().replace(/\s+/g, " ");
  if (pc.length >= 3) {
    params.set("postalcode", pc);
  }

  const url = "https://nominatim.openstreetmap.org/search?" + params.toString();

  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
    },
  });

  if (!res.ok) {
    return { ok: false, error: `HTTP ${res.status}`, hits: [] };
  }

  const data = await res.json();
  const hits = Array.isArray(data) ? data : [];
  const hit = pickBestNominatimHit(st, hits);
  if (!hit || hit.lat == null || hit.lon == null) {
    return { ok: false, error: "No results", hits: [] };
  }

  const lat = Number(hit.lat);
  const lng = Number(hit.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { ok: false, error: "Invalid coordinates", hits: [] };
  }

  return {
    ok: true,
    lat,
    lng,
    displayName: hit.display_name || null,
    hit,
    hits,
  };
}

/**
 * Geocode a Calgary property for parcel intersection: building street line + municipality (+ optional postal).
 * Does not include unit/suite — pass those only in the UI / saved profile line.
 */
export async function geocodeCalgaryParts(
  { streetLine, city = "Calgary", postalCode = "" },
  { delayMs = 1100 } = {}
) {
  const rawStreet = String(streetLine || "").trim();
  const cleaned = addressLineForCalgaryGeocode(rawStreet);
  const street = cleaned || rawStreet;
  const c = String(city || "").trim() || "Calgary";
  const postal = String(postalCode || "").trim();
  const unitStrippedFromPaste = cleaned.length > 0 && cleaned !== rawStreet;

  if (!street || street.length < 4) {
    return {
      ok: false,
      error: "Enter a street address with a civic number.",
      geocodeLineUsed: street,
      unitStripped: unitStrippedFromPaste,
    };
  }

  await sleep(delayMs);

  let result = await nominatimSearchStructured(
    { street, city: c, postalcode: postal },
    { limit: 10 }
  );

  if (!result.ok) {
    const q = `${street}, ${c}, Alberta, Canada${postal ? ` ${postal}` : ""}`;
    await sleep(delayMs);
    result = await nominatimSearchFreeform(q, { limit: 10 });
  }

  if (!result.ok) {
    return {
      ok: false,
      error: result.error || "No results",
      geocodeLineUsed: street,
      unitStripped: unitStrippedFromPaste,
    };
  }

  return {
    ok: true,
    lat: result.lat,
    lng: result.lng,
    displayName: result.displayName || null,
    source: "nominatim",
    geocodeLineUsed: street,
    unitStripped: unitStrippedFromPaste,
  };
}

/**
 * Single-string geocode (admin tools, legacy). Normalizes pasted “unit, street” then uses structured flow.
 */
export async function geocodeCalgaryAddress(streetAddress, { delayMs = 1100 } = {}) {
  const raw = String(streetAddress || "").trim();
  const building = addressLineForCalgaryGeocode(raw);
  const lineForQuery = building || raw;
  const geo = await geocodeCalgaryParts(
    { streetLine: lineForQuery, city: "Calgary", postalCode: "" },
    { delayMs }
  );
  if (!geo.ok) return geo;
  const usedBuildingLine = building.length > 0 && building !== raw;
  return {
    ...geo,
    unitStripped: usedBuildingLine || geo.unitStripped,
  };
}

/** Format one saved profile line (Canada Post–style fields). */
export function formatMyPropertyAddressLine({ unit, street, city, postal }) {
  const u = String(unit ?? "").trim();
  const s = String(street ?? "").trim();
  const c = String(city ?? "").trim() || "Calgary";
  const p = String(postal ?? "").trim().replace(/\s+/g, " ").toUpperCase();

  let line = s;
  if (u) line = `${u}, ${s}`;
  let out = `${line}, ${c}, Alberta`;
  if (p) out += ` ${p}`;
  return out;
}

/** Split a legacy single-line saved address into fields (best-effort). */
export function parseLegacyMyPropertyAddressLine(line) {
  const raw = String(line ?? "").trim();
  if (!raw) {
    return { unit: "", street: "", city: "Calgary", postal: "" };
  }

  let unit = "";
  let work = raw;

  const unitComma = /^(\d{1,4})\s*,\s*(.+)$/;
  const uc = work.match(unitComma);
  if (uc && /^\d/.test(String(uc[2]).trim())) {
    unit = uc[1].trim();
    work = uc[2].trim();
  }

  const lower = work.toLowerCase();
  const calMarker = ", calgary";
  const idx = lower.indexOf(calMarker);
  let street = work;
  let city = "Calgary";
  let postal = "";

  if (idx >= 0) {
    street = work.slice(0, idx).trim();
    let tail = work.slice(idx + calMarker.length).replace(/^,\s*/, "").trim();
    tail = tail.replace(/^alberta\s*,?\s*/i, "").trim();
    const pc = tail.match(/\b([A-Z]\d[A-Z]\s?\d[A-Z]\d)\b/i);
    if (pc) postal = pc[1].replace(/\s+/g, " ").toUpperCase();
  }

  return { unit, street, city, postal };
}
